import React, {useEffect, useState} from 'react';
import CompactTypeSwitch from "./CompactTypeSwitch";

const SECTION_TITLE = {
    common: "항목 설정",
    project: "항목 설정",
    field: "항목 설정",
    groupCode: "항목 설정",
    groupField: "항목 설정",
};

const FIELD_TYPE_OPTIONS = [
    {id: "INPUT", label: "단문"},
    {id: "TEXT", label: "장문"},
    {id: "DATE", label: "날짜"},
];

function CodeEditorReadRow({label, value, required = false, multiline = false}) {
    const display = value != null && String(value).trim() !== "" ? value : "—";

    return (
        <div className={`code-editor__read-row${multiline ? " code-editor__read-row--stack" : ""}`}>
            <span className="code-editor__read-label">
                {label}
                {required && <span className="required">*</span>}
            </span>
            <span className={`code-editor__read-value${multiline ? " code-editor__read-value--multiline" : ""}`}>
                {display}
            </span>
        </div>
    );
}

function CodeEditorEmpty() {
    return (
        <div className="code-editor-empty">
            <div className="code-editor-empty__icon">⌘</div>
            <p className="code-editor-empty__title">항목을 선택하세요</p>
            <p className="code-editor-empty__desc">왼쪽 목록에서 코드를 선택하면 이곳에서 편집할 수 있습니다.</p>
        </div>
    );
}

function CodeEditorPanel({
    category,
    item,
    setItem,
    onSave,
    onDelete,
    deleteTitle = "선택한 항목을 삭제하시겠습니까?",
    deleteDescription = "하위 항목이 있으면 삭제할 수 없습니다.",
    formType,
    nameLabel = "코드명",
    saveLabel = "저장",
    wide = false,
    children,
    extraContent,
    readExtraContent,
    sectionTitle,
    viewMode = false,
    showOrders = true,
}) {
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        setIsEditing(false);
    }, [item?.id]);

    if (!item && !children) {
        return <CodeEditorEmpty/>;
    }

    const metaTitle = sectionTitle ?? (category ? SECTION_TITLE[category] ?? category : null);

    if (children) {
        return (
            <div className={`code-editor ${wide ? "code-editor--wide" : ""}`.trim()}>
                {metaTitle && (
                    <span className="code-editor__meta">{metaTitle}</span>
                )}
                {children}
                {onDelete && item && (
                    <div className="code-editor__actions">
                        <button
                            type="button"
                            className="code-manage-delete-btn"
                            onClick={() => {
                                if (window.confirm(`${deleteTitle}\n${deleteDescription}`)) {
                                    onDelete(item.id);
                                }
                            }}
                        >
                            삭제
                        </button>
                    </div>
                )}
            </div>
        );
    }

    const handleChange = (field, value) => {
        setItem((prev) => ({...prev, [field]: value}));
    };

    const handleSaveClick = () => {
        onSave?.(item);
        if (viewMode) {
            setIsEditing(false);
        }
    };

    const handleDeleteClick = () => {
        if (window.confirm(`${deleteTitle}\n${deleteDescription}`)) {
            onDelete(item.id);
        }
    };

    const formTypeLabel = FIELD_TYPE_OPTIONS.find(
        (option) => option.id === (item.customType ?? "INPUT"),
    )?.label;

    if (viewMode && !isEditing) {
        return (
            <div className="code-editor--readonly bg-[#f6f7f8] flex-1 min-h-0">
                {metaTitle && (
                    <span className="code-editor__meta">{metaTitle}</span>
                )}
                <div className={`code-editor__content`}>
                    <div className="code-editor__read-body code-editor__scroll-body scroll-hidden">
                        <CodeEditorReadRow label={nameLabel} value={item.name} required />
                        <CodeEditorReadRow label="상세설명" value={item.description} multiline />
                        {formType === "string" && (
                            <CodeEditorReadRow label="형식" value={formTypeLabel} />
                        )}
                        {showOrders && formType !== "project" && (
                            <CodeEditorReadRow label="정렬순서" value={item.orders} />
                        )}
                        {readExtraContent}
                    </div>
                    <div className="code-editor__actions">
                        {onDelete && (
                            <button
                                type="button"
                                className="code-manage-delete-btn"
                                onClick={handleDeleteClick}
                            >
                                삭제
                            </button>
                        )}
                        <button
                            type="button"
                            className="save-btn"
                            onClick={() => setIsEditing(true)}
                        >
                            수정
                        </button>
                    </div>
                </div>

            </div>
        );
    }

    return (
        <div className={`code-editor${viewMode ? " code-editor--editing" : ""}`.trim()}>
            {metaTitle && (
                <span className="code-editor__meta">{metaTitle}</span>
            )}
            <div className={`code-editor__content code-editor__scroll-body scroll-hidden`}>
                <div className="">
                    <div className="code-editor__field">
                        <label>
                            {nameLabel}
                            <span className="required">*</span>
                        </label>
                        <input
                            className="basic-input input-style"
                            value={item.name ?? ""}
                            onChange={(e) => handleChange("name", e.target.value)}
                        />
                    </div>

                    <div className="code-editor__field">
                        <label>상세설명</label>
                        <textarea
                            className="basic-text input-style"
                            value={item.description ?? ""}
                            onChange={(e) => handleChange("description", e.target.value)}
                        />
                    </div>

                    {formType === "string" && (
                        <div className="code-editor__field code-editor__field--inline">
                            <span className="code-editor__inline-label">형식</span>
                            <CompactTypeSwitch
                                ariaLabel="필드 형식"
                                options={FIELD_TYPE_OPTIONS}
                                value={item.customType ?? "INPUT"}
                                onChange={(value) => handleChange("customType", value)}
                            />
                        </div>
                    )}

                    {showOrders && formType !== "project" && (
                        <div className="code-editor__field">
                            <label>정렬순서</label>
                            <input
                                className="basic-input input-style"
                                value={item.orders ?? ""}
                                onChange={(e) => handleChange("orders", e.target.value)}
                            />
                        </div>
                    )}

                    {extraContent}
                </div>

                <div className="code-editor__actions">
                    {viewMode ? (
                        <button
                            type="button"
                            className="code-manage-neutral-btn"
                            onClick={() => setIsEditing(false)}
                        >
                            취소
                        </button>
                    ) : onDelete ? (
                        <button
                            type="button"
                            className="code-manage-delete-btn"
                            onClick={handleDeleteClick}
                        >
                            삭제
                        </button>
                    ) : null}
                    <button type="button" className="save-btn" onClick={handleSaveClick}>
                        {saveLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CodeEditorPanel;
